// How a case is identified on screen.
//
// The reviewers asked for FA's EPISODE_ID (2026-08-26). Every screen had been
// labelling a case with its DCN, which is the correlation key across this
// vertical and stays the key — but is a poor thing to put in front of a person:
// DOCUMENT_NUMBER is scoped per FA job, so the same number belongs to different
// patients across clients (HUB-633), and a reviewer reading one off the screen
// cannot look it up in FA.
//
// One helper rather than `c.episodeId || c.id` at each call site, so the
// fallback cannot be got right in the case table and wrong in the review header
// — and so "which identifier is this" has one answer to change.

/** The identifier fields any case-shaped record carries. */
export interface CaseIdentity {
  /** The DCN. The key, and the fallback. */
  id: string;
  /** FA's EPISODE_ID. Empty when no FA leg has landed. */
  episodeId: string;
}

/**
 * What to show as the case's identifier.
 *
 * The episode id where there is one, the DCN where there is not. Never blank:
 * a PDF-only case still has to be nameable, and an empty identity column reads
 * as a broken row rather than as a case FA has not claimed yet.
 */
export function caseLabel(c: CaseIdentity): string {
  return c.episodeId || c.id;
}

/**
 * Whether `caseLabel` fell back to the DCN.
 *
 * The views use it to say WHICH identifier they are showing. Two numbers that
 * look alike and mean different things, silently swapped per row, is worse than
 * either one — a reviewer quoting it to FA needs to know what they are quoting.
 */
export function isFallbackLabel(c: CaseIdentity): boolean {
  return !c.episodeId;
}

/** Tooltip naming the identifier on screen, and the DCN behind it. */
export function caseLabelTitle(c: CaseIdentity): string {
  return isFallbackLabel(c)
    ? `DCN ${c.id} — no FA episode has claimed this case yet`
    : `FA episode ${c.episodeId} · DCN ${c.id}`;
}
